/**
 * Guide -> stations -> one pick per station -> runs -> simplified polylines.
 *
 * There is deliberately NO refinement loop. Feeding the extracted crest back
 * as the next guide was measured and rejected: mean error degraded
 * 495 -> 707 -> 938 -> 1006 mm over three passes and coverage halved on the
 * first, because refining onto a wrong crest drags the guide further off
 * (spec §7.4).
 */
import type { DrapeSampler } from '@/core/alignment';

import { guideStations } from './guide';
import { scanSection } from './sectionScan';
import { buildRun, splitRuns } from './simplify';
import type { CrestParams, CrestPoint, CrestResult, Guide } from './types';

/** Above this share of rejected stations the result is reported as unreliable. */
const MOSTLY_REJECTED_RATIO = 0.5;

/**
 * @param insideBoundary Optional analysis boundary, as a predicate over the
 *   PICKED crest point's XY. Deliberately a plain argument and NOT a field of
 *   `CrestParams`: `CrestParams` is serialised verbatim into a saved feature's
 *   `properties.crest` provenance and has to stay plain, comparable data — a
 *   closure in there would land in the database as `{}`. The chosen layer is
 *   recorded separately, as `properties.crest.boundaryLayerId`.
 *
 *   It is applied at the PICK level, not to the finished polylines: a station
 *   whose crest point falls outside becomes a GAP, so `splitRuns` breaks the
 *   crest into separate runs at the boundary edge instead of welding a
 *   straight segment across the excluded stretch. That is the right behaviour
 *   when a boundary cuts across an embankment rather than ending it.
 */
export function extractCrestLine(
  sampler: DrapeSampler,
  guide: Guide,
  params: CrestParams,
  insideBoundary?: (x: number, y: number) => boolean,
): CrestResult {
  const stations = guideStations(guide, params.stationInterval);
  const picks: Array<CrestPoint | null> = [];
  let prevOffset: number | null = null;
  let flatSections = 0;
  let reliefSections = 0;
  let stationsRejected = 0;
  // Of the rejected stations, how many were dropped by the boundary rather
  // than by the surface scan. Kept apart so the warning below can give the
  // right advice: "widen the corridor" is actively wrong for a station the
  // user's own boundary excluded.
  let stationsOutsideBoundary = 0;

  for (const st of stations) {
    const p = scanSection(st, sampler, params, prevOffset);
    if (p === null) {
      picks.push(null);
      prevOffset = null;
      stationsRejected++;
      continue;
    }
    if (insideBoundary && !insideBoundary(p.x, p.y)) {
      picks.push(null);
      prevOffset = null;
      stationsRejected++;
      stationsOutsideBoundary++;
      continue;
    }
    prevOffset = p.offset;
    if (p.kind === 'flat') flatSections++;
    else reliefSections++;
    picks.push({ ...p, station: st.station });
  }

  const grouped = splitRuns(picks, params);
  const runs = grouped.map((g) => buildRun(g, params));
  const rawVertexCount = grouped.reduce((s, g) => s + g.length, 0);

  let warning: CrestResult['warning'] = null;
  // Stations the SURFACE scan rejected, excluding the ones the boundary cut.
  // Every test below is phrased on this rather than on `stationsRejected` so
  // that, with no boundary supplied (`stationsOutsideBoundary === 0`), the
  // classification is byte-for-byte what it was before boundaries existed.
  const surfaceRejected = stationsRejected - stationsOutsideBoundary;
  // Three very different failures land on `runs.length === 0`, and they need
  // opposite advice — see the CrestWarning docstring. `surfaceRejected ===
  // stations.length` is the honest test for "no surface was ever found";
  // anything else means picks existed and `splitRuns` discarded them all for
  // being shorter than `minRunLength` (or shorter than two stations).
  if (runs.length === 0) {
    if (stationsOutsideBoundary > 0 && stationsOutsideBoundary >= surfaceRejected) {
      warning = 'OUTSIDE_BOUNDARY';
    } else {
      warning = surfaceRejected === stations.length ? 'GUIDE_OFF_SURFACE' : 'RUNS_TOO_SHORT';
    }
  } else if (stations.length > 0 && surfaceRejected / stations.length > MOSTLY_REJECTED_RATIO) {
    // Not `stationsRejected`: a boundary that deliberately trims two thirds of
    // the guide is the user getting what they asked for, not a bad corridor.
    warning = 'MOSTLY_REJECTED';
  } else if (reliefSections === 0) {
    warning = 'ALL_FLAT';
  }

  return {
    runs,
    stationsSampled: stations.length,
    stationsRejected,
    flatSections,
    reliefSections,
    rawVertexCount,
    warning,
  };
}
